import {
  WindowConfig, SectionConfig, SectionType, SectionState,
  MaterialFamily, GlazingTier, RoedenConfig,
} from './types';
import { LAYOUT_PRESETS } from './layout/presets';
import { DEFAULT_COLORS } from './utils/colors';

type Listener = () => void;

export function createDefaultSectionConfig(): SectionConfig {
  return {
    type: SectionType.FIX,
    orientation: {},
    hardware: {
      handleType: 'standard',
      handleFinish: 'chrome',
      handlePosition: 'right',
      restrictor: false,
    },
    infill: {
      type: 'glazing',
      safety: 'none',
      acoustic: false,
      solarControl: false,
    },
    roeden: { mode: 'none', pattern: 'none' },
    openState: SectionState.CLOSED,
    openAmount: 0,
  };
}

export function createDefaultSections(count: number): SectionConfig[] {
  return Array.from({ length: count }, () => createDefaultSectionConfig());
}

function getDefaultOrientation(type: SectionType): SectionConfig['orientation'] {
  switch (type) {
    case SectionType.CAS_IN:
      return { hingeSide: 'left', swingDir: 'inward' };
    case SectionType.CAS_OUT:
      return { hingeSide: 'left', swingDir: 'outward' };
    case SectionType.TT:
      return { hingeSide: 'left' };
    case SectionType.TILT_IN:
      return {};
    case SectionType.TOPHUNG_OUT:
      return {};
    case SectionType.STOLP:
      return { hingeSide: 'left' };
    case SectionType.PIVOT_V:
      return { pivotOffset: 0.5 };
    case SectionType.SLIDE_H:
      return { trackCount: 2 };
    case SectionType.SLIDE_V:
      return { trackCount: 1 };
    default:
      return {};
  }
}

function getDefaultHardware(type: SectionType): SectionConfig['hardware'] {
  const base = {
    handleType: 'standard' as const,
    handleFinish: 'chrome' as const,
    restrictor: false,
  };
  switch (type) {
    case SectionType.CAS_IN:
    case SectionType.CAS_OUT:
      return { ...base, handlePosition: 'right' as const };
    case SectionType.TT:
      return { ...base, handlePosition: 'right' as const };
    case SectionType.TILT_IN:
      return { ...base, handlePosition: 'top' as const };
    case SectionType.TOPHUNG_OUT:
      return { ...base, handlePosition: 'bottom' as const };
    case SectionType.STOLP:
      return { ...base, handlePosition: 'right' as const };
    case SectionType.PIVOT_V:
      return { ...base, handlePosition: 'right' as const };
    default:
      return { ...base, handlePosition: 'right' as const };
  }
}

export function createDefaultConfig(): WindowConfig {
  const preset = LAYOUT_PRESETS['L3T2'];
  return {
    width: 1200,
    height: 1500,
    layout: {
      presetId: 'L3T2',
      expression: preset.expression,
      mirrorLR: false,
      flipTB: false,
    },
    frame: {
      material: MaterialFamily.UPVC,
      insideColor: DEFAULT_COLORS[MaterialFamily.UPVC].inside,
      outsideColor: DEFAULT_COLORS[MaterialFamily.UPVC].outside,
    },
    sections: createDefaultSections(preset.sections),
    glazingTier: GlazingTier.HR_PLUS_PLUS,
    ventilation: { enabled: false, type: 'frame', position: 'top' },
    insectScreen: { enabled: false },
    shading: { enabled: false },
  };
}

export class WindowState {
  config: WindowConfig;
  private listeners: Set<Listener> = new Set();

  constructor(initial?: WindowConfig) {
    this.config = initial ?? createDefaultConfig();
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private notify(): void {
    for (const fn of this.listeners) fn();
  }

  setDimensions(width: number, height: number): void {
    this.config.width = Math.max(300, Math.min(3000, Math.round(width)));
    this.config.height = Math.max(300, Math.min(3000, Math.round(height)));
    this.notify();
  }

  setLayout(presetId: string): void {
    const preset = LAYOUT_PRESETS[presetId];
    if (!preset) return;
    this.config.layout.presetId = presetId;
    this.config.layout.expression = preset.expression;
    this.config.layout.mirrorLR = false;
    this.config.layout.flipTB = false;
    this.config.sections = createDefaultSections(preset.sections);
    this.notify();
  }

  setMirror(mirrorLR: boolean): void {
    this.config.layout.mirrorLR = mirrorLR;
    this.notify();
  }

  setFlip(flipTB: boolean): void {
    this.config.layout.flipTB = flipTB;
    this.notify();
  }

  setSectionType(index: number, type: SectionType): void {
    if (index < 0 || index >= this.config.sections.length) return;
    this.config.sections[index].type = type;
    this.config.sections[index].orientation = getDefaultOrientation(type);
    this.config.sections[index].hardware = getDefaultHardware(type);
    this.config.sections[index].openState = SectionState.CLOSED;
    this.config.sections[index].openAmount = 0;
    this.notify();
  }

  setSectionHinge(index: number, side: 'left' | 'right'): void {
    if (index < 0 || index >= this.config.sections.length) return;
    this.config.sections[index].orientation.hingeSide = side;
    this.notify();
  }

  setSectionOpenState(index: number, state: SectionState, amount: number): void {
    if (index < 0 || index >= this.config.sections.length) return;
    this.config.sections[index].openState = state;
    this.config.sections[index].openAmount = amount;
    this.notify();
  }

  setFrameMaterial(material: MaterialFamily): void {
    this.config.frame.material = material;
    this.config.frame.insideColor = DEFAULT_COLORS[material].inside;
    this.config.frame.outsideColor = DEFAULT_COLORS[material].outside;
    this.notify();
  }

  setFrameColor(side: 'inside' | 'outside', color: string): void {
    if (side === 'inside') this.config.frame.insideColor = color;
    else this.config.frame.outsideColor = color;
    this.notify();
  }

  setGlazingTier(tier: GlazingTier): void {
    this.config.glazingTier = tier;
    this.notify();
  }

  setSectionRoeden(index: number, roeden: RoedenConfig): void {
    if (index < 0 || index >= this.config.sections.length) return;
    this.config.sections[index].roeden = roeden;
    this.notify();
  }

  setHandleFinish(index: number, finish: SectionConfig['hardware']['handleFinish']): void {
    if (index < 0 || index >= this.config.sections.length) return;
    this.config.sections[index].hardware.handleFinish = finish;
    this.notify();
  }

  setVentilation(enabled: boolean): void {
    this.config.ventilation.enabled = enabled;
    this.notify();
  }

  toJSON(): Record<string, unknown> {
    return JSON.parse(JSON.stringify(this.config));
  }
}
